import { useState, useRef, useCallback, useEffect } from 'react';
import { useApplyTheme } from '../context/theme';

import config from '../config';

const usePartyRequest = (formId, requestId) => {
  const [ error, setError ] = useState(null);
  const [ request, setRequest ] = useState(null);
  const [ loading, setLoading ] = useState(true);
  const lastUpdated = useRef(null);

  useApplyTheme(request, 'agency.theme', loading);

  const fetchRequest = useCallback(async () => {
    setLoading(true);

    try {
      const response = await fetch(
        `${config.api}/requests/${formId}/${requestId}`,
        {  
          method: 'GET', 
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }
        }
      );

      if (response.status === 404) {
        var error =  new Error('Request not found. Please contact the agent if further information is required.')
        error._friendly = true;

        throw error;
      }

      const data = await response.json();

      if (data.error) {
        return setError(data.error);
      }

      lastUpdated.current = new Date().getTime();
      setRequest(data);
    } catch (e) {
      setError((e._friendly && e.message) || 'An unknown error occurred fetching the request details');
    } finally {
      setLoading(false);
    }
  }, [ formId, requestId ])

  useEffect(() => {
    fetchRequest();
  }, [ fetchRequest ]);

  return [ loading, request, error, fetchRequest, lastUpdated.current, setRequest ];
}

export const useFiles = (formId, requestId) => {
  async function uploadFile (file, description) {
    const response = await fetch(
      `${config.api}/requests/${formId}/${requestId}/files`,
      { 
        body: JSON.stringify({ ...file, description, print: true }),
        method: 'POST', 
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }
      }
    );

    var body = await response.json();

    if (!response.ok) {
      var uploadError = {
        isFriendly: true, 
        code: 'E_FILE_UPLOAD_UNKNOWN',
        error: 'An upload error occurred',
        message: 'An unknown error occurred uploding the document.'
      }

      if (body.code) {
        Object.assign(uploadError, { message: body.error, code: body.code });
      }

      throw uploadError;
    }
    
    return body;    
  }

 async function removeFile (fileId) {
    const response = await fetch(
      `${config.api}/requests/${formId}/${requestId}/files/${fileId}`,
      { 
        method: 'DELETE', 
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }
      }
    );
    
    if (!response.ok) {
      throw new Error('An error occurred removing the file.')
    };
  }

  return [uploadFile, removeFile];
}

export const useSaveRequest = (formId, requestId, setNotification) => {
  const [ loading, setLoading ] = useState(false);

  const save = useCallback(async (values, completed, onError) => {
    setLoading(true);

    try {
      var data = null;
      var error = null;

      const response = await fetch(
        `${config.api}/requests/${formId}/${requestId}`,
        { 
          body: JSON.stringify({ values, completed }),
          method: 'PUT', 
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }
        }
      );
      
      data  = await response.json() || {};
      error = data.error;
    } catch (e) {
      error = 'An unknown error occurred updating the form';
    } finally {
      var isValid = true;

      if (error) {
        setNotification({
          success: false,
          message: error,
          title: 'An error occurred'
        });

        isValid = false
      }

      setLoading(false);
      return [ isValid, data ];
    }
  }, [ formId, requestId, setNotification ])

  return [ save, loading ];
}

export default usePartyRequest;